/* eslint-disable @typescript-eslint/no-explicit-any */
/** @format */

import { useAuthStore } from "../../features/auth/store";
import { useTenantStore } from "../stores/useTenantStore";
import { getOspreyApiBaseUrl } from "./apiBase";

// Endpoints that require a tenant context (x-tenant-id header)
const TENANT_SCOPED_PREFIXES = [
  "/products",
  "/orders",
  "/inventory",
  "/slider-mapping",
  "/categories",
  "/materials",
  "/colours",
  "/sizes",
  "/size-guides",
  "/dashboard",
];

const isTenantScopedEndpoint = (endpoint: string) =>
  TENANT_SCOPED_PREFIXES.some((prefix) => {
    const path = endpoint.split('?')[0];
    return path === prefix || path.startsWith(prefix + '/');
  });

// Toast notification function
const showToast = (message: string, type: 'error' | 'success' = 'error') => {
  const toast = document.createElement('div');
  toast.className = 'fixed top-4 right-4 z-50 px-4 py-2 rounded-md text-white font-medium ' +
    (type === 'error' ? 'bg-red-500' : 'bg-green-500') +
    ' animate-in slide-in-from-top-2 duration-300';
  toast.textContent = message;
  document.body.appendChild(toast);
  
  setTimeout(() => {
    toast.remove();
  }, 5000);
};

// osprey-api BFF base — the single backend origin the UI talks to. See apiBase.ts.
const getBaseUrl = getOspreyApiBaseUrl;

const getAdminApiKey = (): string => {
  return (window as any).__RUNTIME_CONFIG__?.ADMIN_API_KEY
    ?? import.meta.env.VITE_ADMIN_API_KEY
    ?? '';
};

async function request<T = any>(
  method: string,
  endpoint: string,
  data?: any,
  isFormData?: boolean
): Promise<T> {
  const BASE_URL = getBaseUrl();
  const headers: HeadersInit = {};

  if (!isFormData && data && method !== "GET") {
    headers["Content-Type"] = "application/json";
  }
  headers["Accept"] = "application/json";

  const adminApiKey = getAdminApiKey();
  if (adminApiKey) {
    headers["x-api-key"] = adminApiKey;
  }

  const { token, selectedTenantId } = useAuthStore.getState();
  if (token) {
    headers["Authorization"] = "Bearer " + token;
  }
  if (selectedTenantId) {
    headers["x-tenant-id"] = selectedTenantId;
  }

  // Append tenant context only for tenant-scoped endpoints
  if (isTenantScopedEndpoint(endpoint)) {
    const activeMerchant = useTenantStore.getState().activeMerchant;
    if (activeMerchant?.id) {
      // headers["x-tenant-id"] = activeMerchant.id;
      const separator = endpoint.includes('?') ? '&' : '?';
      endpoint = endpoint + separator + 'tenantId=' + activeMerchant.id;
    }
  }

  const config: RequestInit = {
    method,
    headers,
  };

  if (data && method !== "GET") {
    config.body = isFormData ? data : JSON.stringify(data);
  }

  const res = await fetch(BASE_URL + endpoint, config);

  if (!res.ok) {
    const errorData = await res.json();
    if (res.status === 401) {
      const { logout } = useAuthStore.getState();
      logout();
      showToast('Session expired. Please login again.');
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = "/login?redirect_to=" + encodeURIComponent(currentPath);
      }
    }
    const errorMessage = errorData.message || errorData.errors?.[0]?.message || res.statusText || "Something went wrong";
    const isMerchantRequired = errorData.errorCode === 1600;
    if (res.status !== 401 && !isMerchantRequired) showToast(errorMessage);
    if (isMerchantRequired) useTenantStore.getState().openSelector();
    throw {
      status: res.status,
      message: errorMessage,
      errors: errorData.errors,
      errorCode: errorData.errorCode,
      fullError: errorData
    };
  }
  return res.json();
}

export const ApiFetch = {
  get: <T = any>(endpoint: string) => request<T>("GET", endpoint),
  post: <T = any>(endpoint: string, data?: any, isFormData?: boolean) =>
    request<T>("POST", endpoint, data, isFormData),
  put: <T = any>(endpoint: string, data?: any, isFormData?: boolean) =>
    request<T>("PATCH", endpoint, data, isFormData),
  legacy_put: <T = any>(endpoint: string, data?: any, isFormData?: boolean) =>
    request<T>("PUT", endpoint, data, isFormData),
  del: <T = any>(endpoint: string) => request<T>("DELETE", endpoint),
};
